import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  PULSE_OVERVIEW,
  PULSE_PROJECTS,
  PULSE_PORTALS,
  PULSE_SAMPLES,
  PULSE_INCIDENTS,
  PULSE_HOURLY,
  PULSE_DAILY,
  PULSE_REPORTS,
  PULSE_POLL,
  PULSE_PORTAL_RELIABILITY,
  PULSE_PORTAL_CHECK_HISTORY,
} from "../../../constants/url_helpers";
import { axiosAPI } from "../../../services/api";

export const getPulseOverview = createAsyncThunk(
  "pulse/getOverview",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_OVERVIEW, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseProjects = createAsyncThunk(
  "pulse/getProjects",
  async (_, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_PROJECTS);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const createPulseProject = createAsyncThunk(
  "pulse/createProject",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.post(PULSE_PROJECTS, payload);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const updatePulseProject = createAsyncThunk(
  "pulse/updateProject",
  async ({ id, ...payload }, thunk) => {
    try {
      const res = await axiosAPI.patch(`${PULSE_PROJECTS}${id}/`, payload);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const deletePulseProject = createAsyncThunk(
  "pulse/deleteProject",
  async (id, thunk) => {
    try {
      const res = await axiosAPI.delete(`${PULSE_PROJECTS}${id}/`);
      return { ...res.data, id };
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulsePortals = createAsyncThunk(
  "pulse/getPortals",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_PORTALS, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulsePortal = createAsyncThunk(
  "pulse/getPortal",
  async (id, thunk) => {
    try {
      const res = await axiosAPI.get(`${PULSE_PORTALS}${id}/`);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseReliability = createAsyncThunk(
  "pulse/getReliability",
  async (payload, thunk) => {
    try {
      const id = typeof payload === "object" ? payload.id : payload;
      const date = typeof payload === "object" ? payload.date : undefined;
      const res = await axiosAPI.get(PULSE_PORTAL_RELIABILITY(id), {
        params: date ? { date } : undefined,
      });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseCheckHistory = createAsyncThunk(
  "pulse/getCheckHistory",
  async ({ id, days = 30, date }, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_PORTAL_CHECK_HISTORY(id), {
        params: { days, ...(date ? { date } : {}) },
      });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const createPulsePortal = createAsyncThunk(
  "pulse/createPortal",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.post(PULSE_PORTALS, payload);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const updatePulsePortal = createAsyncThunk(
  "pulse/updatePortal",
  async ({ id, ...payload }, thunk) => {
    try {
      const res = await axiosAPI.patch(`${PULSE_PORTALS}${id}/`, payload);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const deletePulsePortal = createAsyncThunk(
  "pulse/deletePortal",
  async (id, thunk) => {
    try {
      const res = await axiosAPI.delete(`${PULSE_PORTALS}${id}/`);
      return { ...res.data, id };
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseSamples = createAsyncThunk(
  "pulse/getSamples",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_SAMPLES, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseIncidents = createAsyncThunk(
  "pulse/getIncidents",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_INCIDENTS, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseHourly = createAsyncThunk(
  "pulse/getHourly",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_HOURLY, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseDaily = createAsyncThunk(
  "pulse/getDaily",
  async (payload, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_DAILY, { params: payload || {} });
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseReports = createAsyncThunk(
  "pulse/getReports",
  async (_, thunk) => {
    try {
      const res = await axiosAPI.get(PULSE_REPORTS);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const getPulseReport = createAsyncThunk(
  "pulse/getReport",
  async (reportDate, thunk) => {
    try {
      const res = await axiosAPI.get(`${PULSE_REPORTS}${reportDate}/`);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);

export const triggerPulsePoll = createAsyncThunk(
  "pulse/triggerPoll",
  async (_, thunk) => {
    try {
      const res = await axiosAPI.post(PULSE_POLL);
      return res.data;
    } catch (e) {
      return thunk.rejectWithValue(e?.response?.data);
    }
  }
);
